import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import Button from "../Button";
import "./_split-button.scss";

/**
 * A primary CTA fused with an attached dropdown caret ("+ Add company ▾") -
 * the design's split-button pattern for a page's main create action, where
 * the dropdown offers alternate ways to do the same create (e.g. "Import
 * from CSV"). The main button behaves exactly like a plain `Button` (same
 * onClick/disabled/loading), the caret is a separate, narrower hit target
 * that only ever opens `items`.
 *
 * Dropdown open/outside-click logic mirrors `ActionsButton` (the app's other
 * button-with-attached-menu component) rather than introducing a third
 * pattern.
 */
const SplitButton = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  size = "md",
  items = [],
  title,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (item) => {
    if (item.onClick) item.onClick();
    setIsOpen(false);
  };

  return (
    <div className={`split-button ${className}`.trim()} ref={wrapperRef}>
      <Button
        type="button"
        variant="contained"
        size={size}
        onClick={onClick}
        disabled={disabled}
        loading={loading}
        title={title}
        className="split-button__main"
      >
        {children}
      </Button>
      {items.length > 0 && (
        <Button
          type="button"
          variant="contained"
          size={size}
          iconOnly
          icon={<ChevronDown size={13} />}
          disabled={disabled}
          onClick={() => setIsOpen((prev) => !prev)}
          className="split-button__caret"
          aria-label="More options"
          aria-expanded={isOpen}
        />
      )}
      {isOpen && items.length > 0 && (
        <div className="split-button__dropdown">
          {items.map((item, idx) => (
            <button
              key={idx}
              type="button"
              className="split-button__item"
              disabled={item.disabled}
              title={item.title}
              onClick={() => handleItemClick(item)}
            >
              {item.icon && <span className="split-button__item-icon">{item.icon}</span>}
              <span className="split-button__item-text">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SplitButton;
